import { useMemo, useState } from 'react'
import { DataSourceCard } from '../components/settings/DataSourceCard'
import { defaultDataSourceConfigs } from '../data/dataSources'
import { useLocale } from '../i18n/locale'
import { loadDataSourceConfigs, saveDataSourceConfigs } from '../storage/localConfig'
import type { DataSourceConfig, DataSourceCredentials, DataSourceId } from '../types/dataSource'

function getStatus(id: DataSourceId, credentials: DataSourceCredentials) {
  const hasCustomValues = Boolean(credentials.apiKey || credentials.apiSecret || credentials.apiBaseUrl)
  if (id === 'cme-fedwatch') return hasCustomValues ? 'connected' as const : 'not-configured' as const
  return hasCustomValues ? 'connected' as const : 'default' as const
}
export function SettingsPage() {
  const { t } = useLocale()
  const [configs, setConfigs] = useState<DataSourceConfig[]>(() => loadDataSourceConfigs(defaultDataSourceConfigs))
  const [configuredSource, setConfiguredSource] = useState<DataSourceId | null>(null)
  const [draftCredentials, setDraftCredentials] = useState<DataSourceCredentials>({})
  const [saved, setSaved] = useState(false)
  const sourceToConfigure = useMemo(() => configs.find((config) => config.id === configuredSource), [configs, configuredSource])

  function toggleSource(id: DataSourceId) {
    const nextConfigs = configs.map((config) => config.id === id ? { ...config, enabled: !config.enabled } : config)
    setConfigs(nextConfigs)
    saveDataSourceConfigs(nextConfigs)
    setSaved(true)
  }

  function openConfiguration(id: DataSourceId) {
    const source = configs.find((config) => config.id === id)
    setDraftCredentials(source?.credentials ?? {})
    setConfiguredSource(id)
    setSaved(false)
  }

  function updateCredential(key: keyof DataSourceCredentials, value: string) {
    setDraftCredentials((current) => ({ ...current, [key]: value }))
  }

  function saveCredentials() {
    if (!configuredSource) return
    const cleanedCredentials = Object.fromEntries(Object.entries(draftCredentials).filter(([, value]) => value?.trim())) as DataSourceCredentials
    const nextConfigs = configs.map((config) => config.id === configuredSource ? { ...config, credentials: cleanedCredentials, status: getStatus(config.id, cleanedCredentials) } : config)
    setConfigs(nextConfigs)
    saveDataSourceConfigs(nextConfigs)
    setSaved(true)
    setConfiguredSource(null)
  }

  return (
    <div className="page-container settings-page">
      <section className="settings-intro" aria-labelledby="settings-title">
        <div><p className="eyebrow">{t('settings.eyebrow')}</p><h1 id="settings-title">{t('settings.title')}</h1><p>{t('settings.description')}</p></div>
        <div className="settings-intro-readout" aria-label={t('settings.statusAria')}><span>{t('settings.entry')}</span><strong>{t('settings.localOnly')}</strong><small>{saved ? t('settings.saved') : t('settings.unmodified')}</small></div>
      </section>

      <div className="settings-layout">
        <section className="settings-section" aria-labelledby="sources-title">
          <div className="settings-section-heading"><div><p className="eyebrow">{t('settings.publicSources')}</p><h2 id="sources-title">{t('settings.sourcesTitle')}</h2></div><span className="section-note">{t('settings.sourcesNote', { count: configs.length })}</span></div>
          <div className="source-list">{configs.map((config) => <DataSourceCard key={config.id} config={config} onToggle={toggleSource} onConfigure={openConfiguration} />)}</div>
        </section>

        <aside className="settings-aside">
          <div className="aside-module"><p className="eyebrow">{t('settings.systemBoundary')}</p><h2>{t('settings.boundaryTitle')}</h2><p>{t('settings.boundaryBody')}</p></div>
          <div className="aside-module aside-module-rule"><p className="eyebrow">{t('settings.credentialPolicy')}</p><div className="policy-line"><span>{t('settings.storageLocation')}</span><strong>{t('settings.browser')}</strong></div><div className="policy-line"><span>{t('settings.serverUpload')}</span><strong>{t('settings.notUpload')}</strong></div><div className="policy-line"><span>{t('settings.loginAccount')}</span><strong>{t('settings.notRequired')}</strong></div></div>
        </aside>
      </div>

      {sourceToConfigure && <section className="configuration-panel" aria-labelledby="configuration-title">
        <div className="configuration-heading"><p className="eyebrow">{t('settings.configurationEyebrow', { source: sourceToConfigure.name })}</p><h2 id="configuration-title">{t('settings.configurationTitle')}</h2><p>{sourceToConfigure.requiresCredentials ? t('settings.cmeDescription') : t('settings.defaultSourceDescription')}</p><span className="configuration-boundary">{t('settings.configurationBoundary')}</span></div>
        <div className="configuration-form">
          <label><span>{t('settings.apiBaseUrl')}</span><input type="url" value={draftCredentials.apiBaseUrl ?? ''} onChange={(event) => updateCredential('apiBaseUrl', event.target.value)} placeholder={t('settings.leaveBlank')} /></label>
          <label><span>{t('settings.apiKey')}</span><input type="password" value={draftCredentials.apiKey ?? ''} onChange={(event) => updateCredential('apiKey', event.target.value)} placeholder={t('settings.localOnlyPlaceholder')} autoComplete="off" /></label>
          <label><span>{t('settings.apiSecret')}</span><input type="password" value={draftCredentials.apiSecret ?? ''} onChange={(event) => updateCredential('apiSecret', event.target.value)} placeholder={t('settings.localOnlyPlaceholder')} autoComplete="off" /></label>
        </div>
        <div className="configuration-actions"><button className="button button-primary" type="button" onClick={saveCredentials}>{t('settings.saveLocal')}</button><button className="button button-secondary" type="button" onClick={() => setConfiguredSource(null)}>{t('settings.close')}</button></div>
      </section>}

      <div className="settings-footnote"><span className="footer-signal">●</span><p>{t('settings.footnote')}</p></div>
    </div>
  )
}
